import { VoyageSearchItemModel } from 'api-hooks/voyage/model';
import { wjlTimezoneConversion } from 'common/utils/date';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import qs from 'qs';

interface UseMappingTicketItemsRescheduleProps {
  type: 'reschedule';
  data: VoyageSearchItemModel[];
  departureDate: Date;
}

interface UseMappingTicketItemsDefaultProps {
  type: 'default';
  data: VoyageSearchItemModel[];
  facility: number;
}

export default function useMappingTicketItems(
  props:
    | UseMappingTicketItemsDefaultProps
    | UseMappingTicketItemsRescheduleProps,
) {
  const { t } = useTranslation();
  const { query } = useRouter();
  const isVehicle = query.type === '1';
  const id = query.id as string | undefined;

  return props.data.map((item) => {
    let params = '';
    let facility:
      | {
          mattressAvailable: boolean;
          roomAvailable: boolean;
        }
      | undefined = {
      mattressAvailable: !!item.facilityAvailable?.bed,
      roomAvailable: !!item.facilityAvailable?.room,
    };
    if (isVehicle) {
      facility = undefined;
    }
    if (props.type === 'reschedule') {
      params = `/check-booking/${id}/reschedule?${qs.stringify({
        date: format(props.departureDate, 'yyyy-MM-dd'),
        vc: item.voyageCode,
      })}`;
    }

    if (props.type === 'default') {
      params = `/shipping/addons?${qs.stringify({
        ...query,
        facility: props.facility,
        vc: item.voyageCode,
      })}`;
    }

    const arrivalDate = wjlTimezoneConversion(item.arrivedAt, item.arrivedTz);
    const arrivalDayCycle = item.arrivalDayCycle;
    const departureDate = wjlTimezoneConversion(
      item.departureAt,
      item.departureTz,
    );
    const departureDayCycle = item.departureDayCycle;

    const types = ['adult', 'infant'];
    item.passengersSummary.sort(
      (a, b) => types.indexOf(a.name) - types.indexOf(b.name),
    );

    const passenger = (() => {
      const labels: string[] = [];
      item.passengersSummary.forEach((passenger) => {
        labels.push(
          t(`common:${passenger.name}_extra`, {
            extra: `(x${passenger.qty})`,
          }),
        );
      });

      return labels.join(', ');
    })();

    const vehicle = (() => {
      const labels: string[] = [];
      item.vehicleSummary.forEach((vehicle) => {
        const label: string[] = [];
        label.push(vehicle.name);
        if (typeof vehicle.maxHeight === 'string') {
          label.push(
            t('booking:max_height_extra', {
              extra: vehicle.maxHeight,
            }),
          );
        }
        label.push(`(x${vehicle.qty})`);
        labels.push(label.join(' '));
      });

      return labels.join(', ');
    })();

    return {
      query: params,
      facility,
      voyageCode: item.voyageCode,
      arrivalTime: item.arrivalTime,
      arrivalDate,
      arrivalDayCycle,
      chairLefts: item.facilityAvailable?.seat || 0,
      cruiseName: item.shipName,
      departureDate,
      departureTime: item.departureTime,
      departureDayCycle,
      destinationLocation: item.toCity,
      destinationPort: item.toHarbor,
      sourceLocation: item.fromCity,
      sourcePort: item.fromHarbor,
      price: item.priceTotal.toString(),
      passenger,
      vehicle,
    };
  });
}
